// src/components/TaskEdit.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTasks, updateTask } from '../features/tasks/taskSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';

const TaskEdit = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { tasks } = useSelector(state => state.tasks);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [task, setTask] = useState({
        title: '',
        description: '',
        assignee: '',
        due_date: '',
        status: 'pending'
    });

    useEffect(() => {
        // Find the task from Redux state
        const foundTask = tasks.find(t => t.id === taskId);
        if (foundTask) {
            setTask({
                title: foundTask.title || '',
                description: foundTask.description || '',
                assignee: foundTask.assignee || '',
                due_date: foundTask.due_date ? foundTask.due_date.slice(0, 16) : '', // Format for datetime-local
                status: foundTask.status || 'pending'
            });
        } else {
            // If not in Redux, fetch all tasks
            dispatch(fetchTasks());
        }
    }, [taskId, tasks, dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTask(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!task.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        setIsSubmitting(true);
        try {
            // Update the task
            await dispatch(updateTask({
                taskId,
                taskData: task
            })).unwrap();

            alert('Task updated successfully!');
            navigate(`/tasks/${taskId}`);
        } catch (error) {
            alert('Failed to update task: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!task.title && tasks.length === 0) {
        return (
            <div className="edit-task-container">
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading task...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-task-container">
            <div className="edit-task-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Back
                </button>
                <h1>Edit Task</h1>
            </div>

            <div className="edit-task-content">
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="title">Task Title *</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={task.title}
                            onChange={handleInputChange}
                            placeholder="Enter task title..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={task.description}
                            onChange={handleInputChange}
                            placeholder="Describe the task in detail..."
                            rows="5"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="assignee">Assignee</label>
                            <input
                                type="text"
                                id="assignee"
                                name="assignee"
                                value={task.assignee}
                                onChange={handleInputChange}
                                placeholder="Who is responsible?"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                name="status"
                                value={task.status}
                                onChange={handleInputChange}
                            >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="due_date">Due Date</label>
                        <input
                            type="datetime-local"
                            id="due_date"
                            name="due_date"
                            value={task.due_date}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            <FaSave /> {isSubmitting ? 'Updating...' : 'Update Task'}
                        </button>
                    </div>
                </form>
            </div>

            <style jsx>{`
                .edit-task-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }

                .loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    margin-bottom: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .edit-task-header {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e9ecef;
                }

                .back-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #6c757d;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: background 0.3s;
                }

                .back-button:hover {
                    background: #545b62;
                }

                .edit-task-header h1 {
                    margin: 0;
                    color: #333;
                    font-size: 2rem;
                }

                .edit-task-content {
                    background: white;
                    border-radius: 12px;
                    padding: 30px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }

                .form-group {
                    margin-bottom: 25px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #495057;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-group input,
                .form-group textarea,
                .form-group select {
                    width: 100%;
                    padding: 12px 16px;
                    border: 2px solid #e9ecef;
                    border-radius: 8px;
                    font-size: 16px;
                    font-family: inherit;
                    transition: all 0.3s;
                    box-sizing: border-box;
                }

                .form-group input:focus,
                .form-group textarea:focus,
                .form-group select:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .form-group textarea {
                    resize: vertical;
                    min-height: 120px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 15px;
                    margin-top: 30px;
                    padding-top: 25px;
                    border-top: 2px solid #e9ecef;
                }

                .btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s;
                    text-decoration: none;
                }

                .btn-secondary {
                    background: #6c757d;
                    color: white;
                }

                .btn-secondary:hover {
                    background: #545b62;
                }

                .btn-primary {
                    background: #667eea;
                    color: white;
                }

                .btn-primary:hover {
                    background: #5a6fd8;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                @media (max-width: 768px) {
                    .edit-task-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 15px;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .btn {
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default TaskEdit;